import React from "react";
import { Button, ButtonGroup, Dropdown, Spinner } from "react-bootstrap";

export default function OrderProcessingAction({
  item,
  shippingMethod,
  onAccept,
  onCancel,
  onShippingMethodChange,
  onProceedManual,
  onGenerateLabel,

  manualLoading,
  acceptLoading,
  cancelLoading,
}) {
  const status = item?.order_status;
  const currentMethod = shippingMethod || "";

  const labelUrl = item?.label_s3_key;

  // ----------------------------------
  // ORDER PLACED
  // ----------------------------------
  if (status === "Order Placed") {
    return (
      <Dropdown as={ButtonGroup} className="order-action-dropdown">
        <Button
          variant="warning"
          size="sm"
          className="order-status-btn"
          disabled
        >
          {acceptLoading || cancelLoading ? (
            <>
              <Spinner animation="border" size="sm" className="mr-1" />
              Updating...
            </>
          ) : (
            <>
              <i className="fas fa-clock mr-1"></i>
              Action
            </>
          )}
        </Button>

        <Dropdown.Toggle
          split
          variant="warning"
          size="sm"
          className="order-status-toggle"
          disabled={acceptLoading || cancelLoading}
        />

        <Dropdown.Menu align="right" className="order-action-menu">
          <Dropdown.Header>Order Actions</Dropdown.Header>

          <Dropdown.Item
            disabled={acceptLoading || cancelLoading}
            onClick={() => onAccept(item)}
          >
            {acceptLoading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Accepting...
              </>
            ) : (
              <>
                <i className="fas fa-check text-success mr-2"></i>
                Accept Order
              </>
            )}
          </Dropdown.Item>

          <Dropdown.Divider />

          <Dropdown.Item
            disabled={acceptLoading || cancelLoading}
            onClick={() => onCancel(item)}
            className="text-danger"
          >
            {cancelLoading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Cancelling...
              </>
            ) : (
              <>
                <i className="fas fa-times mr-2"></i>
                Cancel Order
              </>
            )}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  // ----------------------------------
  // CREATING ORDER
  // ----------------------------------
  if (status === "Creating Order" && !labelUrl) {
    return (
      <div className="order-processing-action">
        <div className="shipping-method-wrapper">
          <label className="shipping-method-label">
            <i className="fas fa-truck mr-1"></i>
            Shipping
          </label>

          <div className="shipping-method-select-wrapper">
            <select
              className="form-control form-control-sm shipping-method-select"
              value={currentMethod}
              disabled={manualLoading}
              onChange={(e) => onShippingMethodChange(item, e.target.value)}
            >
              <option value="">Select method</option>
              <option value="shipstation">ShipStation</option>
              <option value="manual">Manual</option>
            </select>
          </div>
        </div>

        {/* -------------------------------- */}
        {/* SHIPSTATION */}
        {/* -------------------------------- */}
        {currentMethod === "shipstation" && (
          <div>
            <Button
              variant="primary"
              size="sm"
              className="generate-label-btn"
              onClick={() => onGenerateLabel(item)}
            >
              <i className="fas fa-tag mr-1"></i>
              Generate Label
            </Button>
          </div>
        )}

        {/* -------------------------------- */}
        {/* MANUAL */}
        {/* -------------------------------- */}
        {currentMethod === "manual" && (
          <ManualStatusDropdown
            item={item}
            status={status}
            manualLoading={manualLoading}
            onProceedManual={onProceedManual}
          />
        )}
      </div>
    );
  }

  if (status === "Creating Order" && labelUrl) {
    return(
    <a
      href={labelUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn-sm btn-success w-100"
    >
      <i className="fas fa-file-pdf mr-1"></i>
      View Label
    </a>
    )
  }

  // ----------------------------------
  // SHIPPING
  // ----------------------------------
  if (status === "Shipping" && !labelUrl) {
    return (
      <ManualStatusDropdown
        item={item}
        status={status}
        manualLoading={manualLoading}
        onProceedManual={onProceedManual}
      />
    );
  }

  // ----------------------------------
  // OUT FOR DELIVERY
  // ----------------------------------
  if (status === "Out For Delivery" && !labelUrl) {
    return (
      <ManualStatusDropdown
        item={item}
        status={status}
        manualLoading={manualLoading}
        onProceedManual={onProceedManual}
      />
    );
  } 

  return null;
}

// ======================================================
// MANUAL STATUS DROPDOWN
// ======================================================

function ManualStatusDropdown({
  item,
  status,
  manualLoading,
  onProceedManual,
}) {
  const getNextStatuses = () => {
    switch (status) {
      case "Creating Order":
        return [
          {
            value: "Shipping",
            label: "Shipping",
            icon: "fas fa-box",
            color: "text-primary",
          },
          {
            value: "Out For Delivery",
            label: "Out For Delivery",
            icon: "fas fa-truck",
            color: "text-warning",
          },
          {
            value: "Delivered",
            label: "Delivered",
            icon: "fas fa-check-circle",
            color: "text-success",
          },
        ];

      case "Shipping":
        return [
          {
            value: "Out For Delivery",
            label: "Out For Delivery",
            icon: "fas fa-truck",
            color: "text-warning",
          },
          {
            value: "Delivered",
            label: "Delivered",
            icon: "fas fa-check-circle",
            color: "text-success",
          },
        ];

      case "Out For Delivery":
        return [
          {
            value: "Delivered",
            label: "Delivered",
            icon: "fas fa-check-circle",
            color: "text-success",
          },
        ];

      default:
        return [];
    }
  };

  const nextStatuses = getNextStatuses();

  if (!nextStatuses.length) {
    return null;
  }

  return (
    <Dropdown
      as={ButtonGroup}
      className="manual-action-dropdown"
      disabled={manualLoading}
    >
      <Button size="sm" className="manual-action-btn" disabled={manualLoading}>
        {manualLoading ? (
          <>
            <Spinner animation="border" size="sm" className="mr-1" />
            Updating...
          </>
        ) : (
          <>
            <i className="fas fa-route mr-1"></i>
            Move Order
          </>
        )}
      </Button>

      <Dropdown.Toggle
        split
        size="sm"
        className="manual-action-toggle"
        disabled={manualLoading}
      />

      <Dropdown.Menu align="right">
        <Dropdown.Header>Move order from {status}</Dropdown.Header>

        {nextStatuses.map((nextStatus) => (
          <Dropdown.Item
            key={nextStatus.value}
            disabled={manualLoading}
            onClick={() => onProceedManual(item, nextStatus.value)}
          >
            <i className={`${nextStatus.icon} ${nextStatus.color} mr-2`}></i>

            {nextStatus.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}
